import React, { useState, useRef } from 'react';
import { Sparkles, Eye, Image, Download, Save, Share2, Check } from 'lucide-react';
import VisualQueryBuilder from '../components/football/VisualQueryBuilder';
import StatCardPreview from '../components/football/StatCardPreview';
import html2canvas from 'html2canvas';
import { AZURE } from '../services/api';
import { useAuth } from '../contexts/AuthContext';  // NOVO: Para verificar usuário logado
import { BRAND_GREEN, BRAND_GOLD, BRAND_GREEN_LIGHT } from '../theme/sucronTheme';


const BORDER = '#E5E7EB';

// UI Primitives (padrão AgendaPage/FeedPage)
const PageShell = ({ children }) => (
  <div className="min-h-screen" style={{ backgroundColor: '#f9fafb' }}>
    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
      {children}
    </div>
  </div>
);

const Card = ({ children, className = '' }) => (
  <div
    className={`bg-white rounded-2xl border shadow-sm ${className}`}
    style={{ borderColor: BORDER }}
  >
    {children}
  </div>
);

const CardTitle = ({ children }) => (
  <h3 className="text-sm font-extrabold uppercase tracking-wider" style={{ color: BRAND_GREEN, opacity: 0.7 }}>
    {children}
  </h3>
);

export default function StatsPage() {
  const { user, isAuthenticated } = useAuth();  // NOVO: Verificar autenticação
  const [activeTab, setActiveTab] = useState('build'); // 'build' | 'preview' | 'gallery'
  const [currentInsight, setCurrentInsight] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [savedInsights, setSavedInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [publishingToFeed, setPublishingToFeed] = useState(false);  // NOVO: Estado de publicação
  const [insightSaved, setInsightSaved] = useState(false);  // NOVO: Flag se insight foi salvo no backend
  const [currentInsightId, setCurrentInsightId] = useState(null);  // NOVO: ID do insight no backend
  const cardRef = useRef(null);

  // Buscar dados dos endpoints analytics
  const fetchAnalyticsData = async (template, params) => {
    setLoading(true);
    setError(null);

    try {
      let endpoint = '';
      let payload = {};
      let chartType = '';

      switch (template) {
        case 'player-comparison':
          endpoint = '/football/analytics/player-radar';
          payload = {
            player_ids: params.playerIds.split(',').map(id => parseInt(id.trim())),
            season: params.season || '2024/25',
            dimensions: params.dimensions ? params.dimensions.split(',').map(d => d.trim()) : null
          };
          chartType = 'radar';
          break;

        case 'league-comparison':
          endpoint = '/football/analytics/league-comparison';
          payload = {
            league_ids: params.leagueIds.split(',').map(id => parseInt(id.trim())),
            season: params.season || '2024/25'
          };
          chartType = 'bar';
          break;

        case 'team-style':
          endpoint = '/football/analytics/team-style';
          payload = {
            team_ids: params.teamIds.split(',').map(id => parseInt(id.trim())),
            season: params.season || '2024/25'
          };
          chartType = 'table';
          break;

        case 'match-highlight':
          endpoint = '/football/analytics/match-breakdown';
          payload = {
            match_id: parseInt(params.matchId)
          };
          chartType = 'highlight';
          break;

        default:
          throw new Error('Template não suportado');
      }

      const response = await AZURE.post(endpoint, payload);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Erro ao buscar dados');
      }

      return {
        type: chartType,
        data: response.data.data,
        rawResponse: response.data
      };

    } catch (err) {
      console.error('[StatsPage] Erro ao buscar analytics:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Callback do QueryBuilder
  const handleBuildInsight = async (insightConfig) => {
    setCurrentInsight(insightConfig);

    try {
      const analyticsData = await fetchAnalyticsData(
        insightConfig.template, 
        insightConfig.params
      );

      // Transformar dados para o formato do StatCardPreview
      let previewPayload = {
        type: analyticsData.type,
        title: insightConfig.title,
        subtitle: insightConfig.subtitle,
        data: null
      };

      // Adaptar dados por tipo
      switch (insightConfig.template) {
        case 'player-comparison':
          previewPayload.data = {
            players: analyticsData.data,
            dimensions: insightConfig.params.dimensions 
              ? insightConfig.params.dimensions.split(',').map(d => d.trim())
              : ['goals', 'assists', 'passing', 'duels', 'tackles', 'creativity']
          };
          break;

        case 'league-comparison':
          previewPayload.data = {
            leagues: analyticsData.data,
            metric: insightConfig.params.metric,
            metricLabel: getMetricLabel(insightConfig.params.metric)
          };
          break;

        case 'team-style':
          // Transformar para tabela
          const teams = analyticsData.data;
          previewPayload.data = {
            headers: ['Time', 'Ofensivo', 'Defensivo', 'Posse'],
            rows: teams.map(team => [
              team.team_name,
              team.offensive_score != null ? parseFloat(team.offensive_score).toFixed(1) : '—',
              team.defensive_score != null ? parseFloat(team.defensive_score).toFixed(1) : '—',
              team.possession_avg != null ? parseFloat(team.possession_avg).toFixed(1) + '%' : '—'
            ])
          };
          break;

        case 'match-highlight':
          // Pegar métrica específica
          const matchData = analyticsData.data;
          const metric = insightConfig.params.metric;
          previewPayload.data = {
            stat: matchData[metric] || '—',
            label: getMetricLabel(metric),
            player: matchData.team_name || 'Time',
            context: matchData.match_date || 'Partida'
          };
          break;
      }

      setPreviewData(previewPayload);
      setActiveTab('preview');

    } catch (err) {
      setError(err.message || 'Erro ao construir insight');
    }
  };

  // Helper para labels de métricas
  const getMetricLabel = (metric) => {
    const labels = {
      goals_per_game: 'Gols por Jogo',
      shots_per_game: 'Chutes por Jogo',
      pass_accuracy: 'Precisão de Passe (%)',
      possession_avg: 'Posse Média (%)',
      cards_per_game: 'Cartões por Jogo',
      goals: 'Gols',
      shots: 'Chutes',
      possession: 'Posse de Bola',
      passes: 'Passes',
      fouls: 'Faltas'
    };
    return labels[metric] || metric;
  };

  // Download como imagem
  const handleDownload = async () => {
    if (!cardRef.current) return;

    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Maior qualidade
        logging: false
      });

      const link = document.createElement('a');
      link.download = `arenastats-insight-${Date.now()}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (err) {
      console.error('[StatsPage] Erro ao fazer download:', err);
      alert('Erro ao gerar imagem');
    }
  };

  // Salvar na galeria
  const handleSaveToGallery = async () => {
    if (!previewData) return;

    // Se usuário não está logado, salvar apenas no localStorage (fallback)
    if (!isAuthenticated || !user) {
      saveToLocalStorage();
      return;
    }

    try {
      // Salvar no backend
      const response = await AZURE.post('/posts/insights/save', {
        template: currentInsight.template,
        title: previewData.title,
        subtitle: previewData.subtitle,
        chartType: previewData.type,
        chartData: previewData.data,
        params: currentInsight.params
      });

      if (response.data.success) {
        setCurrentInsightId(response.data.insight_id);
        setInsightSaved(true);
        
        // Adicionar à lista local para exibição imediata
        const newInsight = {
          id: response.data.insight_id,
          ...previewData,
          createdAt: new Date().toISOString(),
          is_published: 0
        };
        
        setSavedInsights(prev => [newInsight, ...prev]);
        
        alert('✅ Insight salvo na galeria com sucesso!');
      }
    } catch (err) {
      console.error('[StatsPage] Erro ao salvar insight:', err);
      // Fallback para localStorage em caso de erro
      saveToLocalStorage();
      alert('⚠️ Salvo localmente. Entre ou registre-se para salvar na nuvem.');
    }
  };

  // Fallback: salvar no localStorage (usuários não logados)
  const saveToLocalStorage = () => {
    const newInsight = {
      id: Date.now(),
      ...previewData,
      createdAt: new Date().toISOString()
    };

    setSavedInsights(prev => [newInsight, ...prev]);
    
    try {
      const stored = JSON.parse(localStorage.getItem('arenastats_insights') || '[]');
      stored.push(newInsight);
      localStorage.setItem('arenastats_insights', JSON.stringify(stored));
      alert('✅ Insight salvo localmente!');
    } catch (err) {
      console.error('[StatsPage] Erro ao salvar no localStorage:', err);
    }
  };

  // NOVO: Compartilhar no Feed Social
  const handleShareToFeed = async () => {
    if (!isAuthenticated || !user) {
      alert('⚠️ Você precisa estar logado para compartilhar no feed!');
      return;
    }

    if (!insightSaved) {
      alert('⚠️ Salve o insight na galeria antes de compartilhar!');
      return;
    }

    setPublishingToFeed(true);

    try {
      // 1. Gerar imagem do card
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false
      });

      const imageDataUrl = canvas.toDataURL('image/png');

      // 2. Publicar no feed (endpoint espera o insight_id e opcionalmente imageUrl)
      const response = await AZURE.post(`/posts/insights/${currentInsightId}/publish`, {
        imageUrl: imageDataUrl  // TODO: Upload real para storage (Fase 3)
      });

      if (response.data.success) {
        alert(`🎉 Insight publicado no feed! Post ID: ${response.data.post_id}`);
        
        // Atualizar status do insight
        setSavedInsights(prev => prev.map(insight => 
          insight.id === currentInsightId 
            ? { ...insight, is_published: 1 } 
            : insight
        ));

        // Opcional: Redirecionar para o feed
        // window.location.href = '/feed';
      }
    } catch (err) {
      console.error('[StatsPage] Erro ao compartilhar no feed:', err);
      alert('❌ Erro ao compartilhar. Tente novamente.');
    } finally {
      setPublishingToFeed(false);
    }
  };

  // Carregar insights salvos (backend se logado, localStorage se não)
  React.useEffect(() => {
    loadGalleryInsights();
  }, [isAuthenticated]);

  const loadGalleryInsights = async () => {
    if (isAuthenticated && user) {
      // Buscar do backend
      try {
        const response = await AZURE.get('/posts/insights?limit=50');
        if (response.data.success) {
          setSavedInsights(response.data.insights);
        }
      } catch (err) {
        console.error('[StatsPage] Erro ao carregar insights do backend:', err);
        // Fallback para localStorage
        loadFromLocalStorage();
      }
    } else {
      // Usuário não logado: carregar do localStorage
      loadFromLocalStorage();
    }
  };

  const loadFromLocalStorage = () => {
    try {
      const stored = JSON.parse(localStorage.getItem('arenastats_insights') || '[]');
      setSavedInsights(stored);
    } catch (err) {
      console.error('[StatsPage] Erro ao carregar insights:', err);
    }
  };

  // Renderizar tabs
  const renderTabs = () => (
    <div className="flex items-center gap-2 mb-8">
      <button
        onClick={() => setActiveTab('build')}
        className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition ${
          activeTab === 'build'
            ? 'text-white shadow-lg'
            : 'bg-white text-gray-600 hover:bg-gray-50'
        }`}
        style={activeTab === 'build' ? { backgroundColor: BRAND_GREEN } : {}}
      >
        <Sparkles size={20} />
        <span>Construir</span>
      </button>

      <button
        onClick={() => setActiveTab('preview')}
        disabled={!previewData}
        className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition ${
          activeTab === 'preview'
            ? 'text-white shadow-lg'
            : 'bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'
        }`}
        style={activeTab === 'preview' ? { backgroundColor: BRAND_GREEN } : {}}
      >
        <Eye size={20} />
        <span>Preview</span>
      </button>

      <button
        onClick={() => setActiveTab('gallery')}
        className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition ${
          activeTab === 'gallery'
            ? 'text-white shadow-lg'
            : 'bg-white text-gray-600 hover:bg-gray-50'
        }`}
        style={activeTab === 'gallery' ? { backgroundColor: BRAND_GREEN } : {}}
      >
        <Image size={20} />
        <span>Galeria ({savedInsights.length})</span>
      </button>
    </div>
  );

  // Renderizar conteúdo da tab Build
  const renderBuildTab = () => (
    <Card className="p-6">
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-700 font-medium">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-sm text-red-600 underline mt-2"
          >
            Fechar
          </button>
        </div>
      )}

      {loading && (
        <div className="rounded-lg p-4 mb-6" style={{ backgroundColor: BRAND_GREEN_LIGHT, borderColor: BRAND_GREEN, borderWidth: '2px' }}>
          <p className="font-medium" style={{ color: BRAND_GREEN }}>Carregando dados analytics...</p>
        </div>
      )}

      <VisualQueryBuilder onBuild={handleBuildInsight} />
    </Card>
  );

  // Renderizar conteúdo da tab Preview
  const renderPreviewTab = () => {
    if (!previewData) {
      return (
        <Card className="p-20 text-center">
          <Sparkles size={64} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-bold text-gray-400 mb-2">
            Nenhum insight em preview
          </h3>
          <p className="text-gray-500 mb-6">
            Construa um insight na aba "Construir" primeiro
          </p>
          <button
            onClick={() => setActiveTab('build')}
            className="px-6 py-3 rounded-lg text-white font-semibold"
            style={{ backgroundColor: BRAND_GREEN }}
          >
            Ir para Construir
          </button>
        </Card>
      );
    }

    return (
      <Card className="p-6">
        {/* Ações */}
        <div className="flex flex-wrap items-center justify-end gap-4 mb-6">
          {/* Salvar na Galeria */}
          <button
            onClick={handleSaveToGallery}
            disabled={insightSaved}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
              insightSaved 
                ? 'cursor-not-allowed'
                : 'bg-white hover:bg-green-50'
            }`}
            style={insightSaved 
              ? { backgroundColor: BRAND_GREEN_LIGHT, color: BRAND_GREEN, borderWidth: '2px', borderColor: BRAND_GREEN }
              : { borderWidth: '2px', borderColor: BRAND_GREEN, color: BRAND_GREEN }
            }
          >
            {insightSaved ? <Check size={18} /> : <Save size={18} />}
            <span>{insightSaved ? 'Salvo' : 'Salvar na Galeria'}</span>
          </button>

          {/* Compartilhar no Feed - NOVO */}
          <button
            onClick={handleShareToFeed}
            disabled={!insightSaved || publishingToFeed || !isAuthenticated}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition ${
              !insightSaved || !isAuthenticated
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-2 border-gray-200'
                : publishingToFeed
                ? 'bg-blue-100 text-blue-600 border-2 border-blue-300'
                : 'bg-blue-600 text-white hover:bg-blue-700 border-2 border-blue-600'
            }`}
            title={!isAuthenticated ? 'Faça login para compartilhar' : !insightSaved ? 'Salve antes de compartilhar' : ''}
          >
            <Share2 size={18} className={publishingToFeed ? 'animate-pulse' : ''} />
            <span>
              {publishingToFeed ? 'Publicando...' : 'Compartilhar no Feed'}
            </span>
          </button>

          {/* Download Imagem */}
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-semibold hover:opacity-90 transition"
            style={{ backgroundColor: BRAND_GOLD }}
          >
            <Download size={18} />
            <span>Download Imagem</span>
          </button>
        </div>

        {/* Aviso de Login */}
        {!isAuthenticated && (
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800 text-sm">
              <strong>💡 Dica:</strong> Faça <a href="/login" className="underline font-bold">login</a> para salvar seus insights na nuvem e compartilhar no feed social!
            </p>
          </div>
        )}

        {/* Preview Card */}
        <div className="flex justify-center">
          <StatCardPreview
            {...previewData}
            onDownload={(ref) => { cardRef.current = ref.current; }}
          />
        </div>
      </Card>
    );
  };

  // Renderizar conteúdo da tab Gallery
  const renderGalleryTab = () => {
    if (savedInsights.length === 0) {
      return (
        <Card className="p-20 text-center">
          <Image size={64} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-bold text-gray-400 mb-2">
            Galeria vazia
          </h3>
          <p className="text-gray-500 mb-4">
            Insights salvos aparecerão aqui
          </p>
          {!isAuthenticated && (
            <p className="text-sm text-yellow-600 bg-yellow-50 inline-block px-4 py-2 rounded-lg">
              💡 Faça <a href="/login" className="underline font-bold">login</a> para sincronizar sua galeria
            </p>
          )}
        </Card>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {savedInsights.map(insight => (
          <Card
            key={insight.id}
            className="overflow-hidden hover:shadow-xl transition cursor-pointer relative"
            onClick={() => {
              setPreviewData(insight);
              setCurrentInsightId(insight.id);
              setInsightSaved(true);
              setActiveTab('preview');
            }}
          >
            {/* Badge de Publicado */}
            {insight.is_published === 1 && (
              <div className="absolute top-3 right-3 z-10 bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-md">
                <Share2 size={12} />
                <span>PUBLICADO</span>
              </div>
            )}

            <div 
              className="px-4 py-3"
              style={{ background: `linear-gradient(135deg, ${BRAND_GREEN} 0%, #374151 100%)` }}
            >
              <h3 className="text-lg font-bold text-white truncate">
                {insight.title}
              </h3>
              <p className="text-sm text-green-100 truncate">
                {insight.subtitle}
              </p>
            </div>
            <div className="p-4">
              <div className="text-xs text-gray-500 mb-2">
                {new Date(insight.createdAt || insight.created_at).toLocaleString('pt-BR')}
              </div>
              <div className="h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-gray-400 text-sm font-medium">
                  Tipo: {insight.type || insight.chart_type}
                </span>
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <PageShell>
      {/* Header */}
      <Card className="p-8 mb-6" style={{ background: `linear-gradient(135deg, ${BRAND_GREEN} 0%, #374151 100%)` }}>
        <h1 className="text-4xl font-black mb-2 text-white">
          Editorial Engine
        </h1>
        <p className="text-lg text-green-100">
          Crie insights profissionais de futebol com dados reais
        </p>
      </Card>

        {/* Tabs */}
        {renderTabs()}

        {/* Conteúdo da Tab Ativa */}
        <div>
          {activeTab === 'build' && renderBuildTab()}
          {activeTab === 'preview' && renderPreviewTab()}
          {activeTab === 'gallery' && renderGalleryTab()}
        </div>
    </PageShell>
  );
}
